// src/components/profile/EducationHistory.tsx
// Komponen riwayat pendidikan dengan timeline layout

import React from "react";
import { GraduationCap } from "lucide-react";
import { useOptimizedProfile } from "../../hooks/useOptimizedProfile";
import { EducationHistory as Education } from "../../types";
import SpotlightCard from "../SpotlightCard/SpotlightCard";
import { PhotoProvider, PhotoView } from 'react-photo-view';
import 'react-photo-view/dist/react-photo-view.css';

const EducationHistory: React.FC = () => {
    const { profileData, loading, error } = useOptimizedProfile();
    const educationHistory = profileData?.educationHistory || [];

    // Fungsi untuk format periode pendidikan
    const formatPeriod = (education: Education): string => {
        const startYear = education.startYear;
        if (!education.endYear || education.endYear > new Date().getFullYear()) {
            return `${startYear} - Sekarang`;
        }
        const endYear = education.endYear;
        return `${startYear} - ${endYear}`;
    };

    // Fungsi untuk mendapatkan warna berdasarkan level pendidikan
    const getEducationColor = (level: string) => {
        const normalizedLevel = level.toLowerCase();
        switch (normalizedLevel) {
            case "kb":
            case "tk":
            case "paud":
            case "playgroup":
            case "taman kanak-kanak":
                return {
                    bg: "bg-pink-50",
                    border: "border-pink-200",
                    icon: "text-pink-600",
                    dot: "bg-pink-500",
                };
            case "sd":
            case "sekolah dasar":
                return {
                    bg: "bg-green-50",
                    border: "border-green-200",
                    icon: "text-green-600",
                    dot: "bg-green-500",
                };
            case "smp":
            case "sekolah menengah pertama":
                return {
                    bg: "bg-blue-50",
                    border: "border-blue-200",
                    icon: "text-blue-600",
                    dot: "bg-blue-500",
                };
            case "sma":
            case "smk":
            case "sekolah menengah atas":
            case "sekolah menengah kejuruan":
                return {
                    bg: "bg-purple-50",
                    border: "border-purple-200",
                    icon: "text-purple-600",
                    dot: "bg-purple-500",
                };
            case "universitas":
            case "perguruan tinggi":
            case "s1":
            case "s2":
            case "s3":
            case "diploma":
                return {
                    bg: "bg-orange-50",
                    border: "border-orange-200",
                    icon: "text-orange-600",
                    dot: "bg-orange-500",
                };
            default:
                return {
                    bg: "bg-gray-50",
                    border: "border-gray-200",
                    icon: "text-gray-600",
                    dot: "bg-gray-500",
                };
        }
    };

    // Function to categorize education levels
    const categorizeEducation = (level: string) => {
        const normalizedLevel = level.toLowerCase();
        if (
            ["kb", "tk", "paud", "playgroup", "taman kanak-kanak"].includes(
                normalizedLevel
            )
        ) {
            return "preschool";
        } else if (["sd", "sekolah dasar"].includes(normalizedLevel)) {
            return "sd";
        } else if (
            ["smp", "sekolah menengah pertama"].includes(normalizedLevel)
        ) {
            return "smp";
        } else if (
            [
                "sma",
                "smk",
                "sekolah menengah atas",
                "sekolah menengah kejuruan",
            ].includes(normalizedLevel)
        ) {
            return "sma_smk";
        } else if (
            [
                "universitas",
                "perguruan tinggi",
                "s1",
                "s2",
                "s3",
                "diploma",
            ].includes(normalizedLevel)
        ) {
            return "universitas";
        }
        return "other";
    };

    // Calculate education statistics
    const educationStats = {
        preschool: educationHistory.filter(
            (e) => categorizeEducation(e.level) === "preschool"
        ).length,
        sd: educationHistory.filter(
            (e) => categorizeEducation(e.level) === "sd"
        ).length,
        smp: educationHistory.filter(
            (e) => categorizeEducation(e.level) === "smp"
        ).length,
        sma_smk: educationHistory.filter(
            (e) => categorizeEducation(e.level) === "sma_smk"
        ).length,
        universitas: educationHistory.filter(
            (e) => categorizeEducation(e.level) === "universitas"
        ).length,
    };

    if (loading) {
        return (
            <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8">
                <div className="flex items-center justify-between mb-8">
                    <div className="h-8 bg-gray-200 rounded w-48 animate-pulse"></div>
                    <div className="h-6 bg-gray-200 rounded w-24 animate-pulse"></div>
                </div>
                <div className="space-y-8">
                    {[1, 2, 3].map((i) => (
                        <div key={i} className="relative flex items-start">
                            <div className="w-16 h-16 bg-gray-200 rounded-full animate-pulse"></div>
                            <div className="ml-6 flex-1 p-6 bg-gray-50 rounded-xl">
                                <div className="h-6 bg-gray-200 rounded w-3/4 mb-3 animate-pulse"></div>
                                <div className="h-4 bg-gray-200 rounded w-1/2 mb-4 animate-pulse"></div>
                                <div className="h-4 bg-gray-200 rounded w-full animate-pulse"></div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-white rounded-2xl shadow-lg p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">
                    Riwayat Pendidikan
                </h2>
                <div className="text-center py-12">
                    <GraduationCap className="h-16 w-16 text-red-300 mx-auto mb-4" />
                    <p className="text-red-500 text-lg">
                        Error memuat data pendidikan: {error}
                    </p>
                </div>
            </div>
        );
    }

    if (!educationHistory || educationHistory.length === 0) {
        return (
            <div className="bg-white rounded-2xl shadow-lg p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">
                    Riwayat Pendidikan
                </h2>
                <div className="text-center py-12">
                    <GraduationCap className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500 text-lg">
                        Belum ada riwayat pendidikan yang ditambahkan
                    </p>
                </div>
            </div>
        );
    }

    // Urutkan pendidikan berdasarkan tahun mulai (terbaru dulu)
    const sortedEducation = [...educationHistory].sort((a, b) => {
        const yearA = a.startYear;
        const yearB = b.startYear;
        return yearB - yearA;
    });

    return (
        <PhotoProvider>
            <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-6 md:p-8">
            {/* Header */}
            <div className="flex items-center justify-between mb-6 sm:mb-8">
                <h2 className="text-xl sm:text-2xl font-bold text-gray-900">
                    Riwayat Pendidikan
                </h2>
                <div className="flex items-center text-xs sm:text-sm text-gray-500">
                    <GraduationCap className="h-4 w-4 mr-1 text-blue-500" />
                    <span>{educationHistory.length} institusi</span>
                </div>
            </div>

            {/* Timeline */}
            <div className="relative">
                {/* Timeline Line */}
                <div className="absolute left-4 sm:left-7 top-2 bottom-2 w-0.5 bg-gradient-to-b from-blue-200 via-purple-200 to-green-200"></div>

                {/* Education Items */}
                <div className="space-y-4 sm:space-y-8">
                    {sortedEducation.map((education) => {
                        const colors = getEducationColor(education.level);

                        return (
                            <div
                                key={education.id}
                                className="relative flex items-start group"
                            >
                                {/* Timeline Dot */}
                                <div
                                    className={`
                      relative z-10 flex items-center justify-center w-8 h-8 sm:w-14 sm:h-14 rounded-full border-2 sm:border-4 border-white shadow-md sm:shadow-lg
                      transition-transform duration-300 group-hover:scale-110 flex-shrink-0
                      ${colors.dot} 
                    `}
                                >
                                    <GraduationCap
                                        className={`h-4 w-4 sm:h-6 sm:w-6 text-white`}
                                    />
                                </div>

                                {/* SpotlightCard - Education Info */}
                                <div className="ml-3 sm:ml-6 flex-1 min-w-0">
                                    <SpotlightCard
                                        className="bg-white border border-gray-200 rounded-xl shadow-xs sm:shadow-md hover:shadow-lg transition-shadow duration-300"
                                        spotlightColor="rgba(59, 130, 246, 0.15)"
                                    >
                                        <div className="p-3.5 sm:p-6">
                                            <div className="flex flex-col-reverse sm:flex-row sm:items-start justify-between gap-3 sm:gap-4">
                                                <div className="flex-1 min-w-0">
                                                    {/* Badges row */}
                                                    <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 mb-1.5 sm:mb-2">
                                                        <span
                                                            className={`inline-flex items-center px-2 sm:px-2.5 py-0.5 rounded-full text-xs font-semibold ${colors.icon} ${colors.bg} border ${colors.border}`}
                                                        >
                                                            {education.level}
                                                        </span>
                                                        {education.is_current && (
                                                            <span className="inline-flex items-center px-2 sm:px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                                                Sedang Berlangsung
                                                            </span>
                                                        )}
                                                    </div>

                                                    {/* School Name */}
                                                    <h3 className="text-base sm:text-xl font-bold text-gray-900 leading-snug break-words">
                                                        {education.school}
                                                    </h3>

                                                    {/* Period */}
                                                    <p className="text-xs sm:text-sm text-gray-600 font-medium mt-1">
                                                        {formatPeriod(
                                                            education
                                                        )}
                                                    </p>

                                                    {/* Description */}
                                                    {education.description && (
                                                        <p className="text-xs sm:text-sm text-gray-500 mt-2 break-words leading-relaxed">
                                                            {
                                                                education.description
                                                            }
                                                        </p>
                                                    )}
                                                </div>

                                                {/* Thumbnail Image */}
                                                {education.image_url && (
                                                    <div className="flex-shrink-0 self-start sm:self-center">
                                                        <PhotoView src={education.image_url}>
                                                            <img
                                                                src={education.image_url}
                                                                alt={`Logo ${education.school}`}
                                                                className="w-12 h-12 sm:w-16 sm:h-16 rounded-xl object-cover cursor-pointer hover:opacity-80 transition-opacity duration-200 border border-gray-200 shadow-xs"
                                                            />
                                                        </PhotoView>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </SpotlightCard>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Education Statistics */}
            <div className="mt-6 sm:mt-8 pt-4 sm:pt-6 border-t border-gray-200">
                <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3">
                    Berdasarkan Jenjang
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 sm:gap-4">
                    {/* TK & KB */}
                    <div className="text-center p-2.5 sm:p-3 rounded-xl bg-gray-50 sm:bg-transparent hover:bg-pink-50 transition-colors duration-200 border sm:border-0 border-gray-100">
                        <div className="text-xl sm:text-2xl font-bold text-pink-600">
                            {educationStats.preschool}
                        </div>
                        <div className="text-xs sm:text-sm text-gray-600">TK & KB</div>
                    </div>

                    {/* SD */}
                    <div className="text-center p-2.5 sm:p-3 rounded-xl bg-gray-50 sm:bg-transparent hover:bg-green-50 transition-colors duration-200 border sm:border-0 border-gray-100">
                        <div className="text-xl sm:text-2xl font-bold text-green-600">
                            {educationStats.sd}
                        </div>
                        <div className="text-xs sm:text-sm text-gray-600">SD</div>
                    </div>

                    {/* SMP */}
                    <div className="text-center p-2.5 sm:p-3 rounded-xl bg-gray-50 sm:bg-transparent hover:bg-blue-50 transition-colors duration-200 border sm:border-0 border-gray-100">
                        <div className="text-xl sm:text-2xl font-bold text-blue-600">
                            {educationStats.smp}
                        </div>
                        <div className="text-xs sm:text-sm text-gray-600">SMP</div>
                    </div>

                    {/* SMA/SMK */}
                    <div className="text-center p-2.5 sm:p-3 rounded-xl bg-gray-50 sm:bg-transparent hover:bg-purple-50 transition-colors duration-200 border sm:border-0 border-gray-100">
                        <div className="text-xl sm:text-2xl font-bold text-purple-600">
                            {educationStats.sma_smk}
                        </div>
                        <div className="text-xs sm:text-sm text-gray-600">SMA/SMK</div>
                    </div>

                    {/* Universitas */}
                    <div className="text-center p-2.5 sm:p-3 rounded-xl bg-gray-50 sm:bg-transparent hover:bg-orange-50 transition-colors duration-200 border sm:border-0 border-gray-100 col-span-2 sm:col-span-1">
                        <div className="text-xl sm:text-2xl font-bold text-orange-600">
                            {educationStats.universitas}
                        </div>
                        <div className="text-xs sm:text-sm text-gray-600">Universitas</div>
                    </div>
                </div>
            </div>
            </div>
        </PhotoProvider>
    );
};

export default EducationHistory;
